import { User } from '../../_models/user';
import { FC, useEffect, useState } from 'react';
import AuthenticationService from '../../_services/AuthenticationService';
import ApiService from '../../_services/ApiService';
import Config from '../../_config/config';
import { useITeachConfig } from '../../_context/PresetContext';
import './tab-profile.css';
import { useHistory } from 'react-router';

export const TabProfile: React.FC = () => {
  const [currentUser, setCurrentUser] = useState<User>();
  const [stats, setStats] = useState<{ activeCourses: string | number; totalStudents: string | number; pendingTasks: string | number; hoursThisWeek: string | number; photoFile: string | null }>({ activeCourses: '-', totalStudents: '-', pendingTasks: '-', hoursThisWeek: '-', photoFile: null });
  const History = useHistory();
  const { config } = useITeachConfig();
  const tenantName = config?.displayName || Config.tenantDisplayName;

  useEffect(() => {
    setCurrentUser(AuthenticationService.currentUserValue);
    ApiService.getInstructorProfileStats()
      .then((res: any) => {
        if (res.data?.success) {
          const d = res.data.data;
          setStats({
            activeCourses: d.activeCourses,
            totalStudents: d.totalStudents,
            pendingTasks: d.pendingTasks,
            hoursThisWeek: d.hoursThisWeek,
            photoFile: d.photoFile || null,
          });
        }
      })
      .catch(() => {});
  }, []);

  async function handleLogout() {
    await AuthenticationService.logout();
    History.push('/login');
  }

  const instructorId = (currentUser as any)?.instructorId;
  const userProfilePic = stats.photoFile || null;

  // Try multiple possible username fields from the JWT token
  const userName = currentUser?.instructorName ||
                   (currentUser as any)?.username ||
                   currentUser?.userName ||
                   (currentUser as any)?.emailId ||
                   'User';
  const userEmail = (currentUser as any)?.userEmailId ||
                    currentUser?.emailId ||
                    (currentUser as any)?.user_email ||
                    currentUser?.userName ||
                    '';
  const userRole = (currentUser as any)?.role ||
                   (currentUser as any)?.userTypeId === 1 ? 'Admin' : 'Instructor';

  return (
    <div className="profile-page">
      <div className="profile-container">
        {/* Back Button */}
        <div className="profile-back-nav">
          <button onClick={() => History.push('/home')} className="profile-back-btn">
            ← Back to Home
          </button>
        </div>

        {/* Header Section */}
        <div className="profile-header">
          <div className="profile-header-bg"></div>
          <div className="profile-header-content">
            <div className="profile-avatar-wrapper">
              {userProfilePic ? (
                <img
                  src={userProfilePic}
                  alt={userName}
                  className="profile-avatar"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = require('../../assets/img/blankAvatar.png');
                  }}
                />
              ) : (
                <img
                  src={require('../../assets/img/blankAvatar.png')}
                  alt={userName}
                  className="profile-avatar"
                />
              )}
              <div className="profile-status-indicator"></div>
            </div>
            <div className="profile-header-info">
              <h1 className="profile-name">{userName}</h1>
              <p className="profile-role">{userRole}</p>
              <p className="profile-email">{userEmail}</p>
              <p className="profile-institute">{tenantName}</p>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="profile-content">
          {/* Account Information Card */}
          <div className="profile-card">
            <div className="profile-card-header">
              <h2>Account Information</h2>
            </div>
            <div className="profile-card-body">
              <div className="profile-info-grid">
                <div className="profile-info-item">
                  <label>Full Name</label>
                  <div className="profile-info-value">{userName}</div>
                </div>
                <div className="profile-info-item">
                  <label>Email / Username</label>
                  <div className="profile-info-value">{userEmail}</div>
                </div>
                <div className="profile-info-item">
                  <label>Role</label>
                  <div className="profile-info-value">{userRole}</div>
                </div>
                <div className="profile-info-item">
                  <label>Instructor ID</label>
                  <div className="profile-info-value">{instructorId || 'N/A'}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Stats Card */}
          <div className="profile-card">
            <div className="profile-card-header">
              <h2>Quick Stats</h2>
            </div>
            <div className="profile-card-body">
              <div className="profile-stats-grid">
                <div className="profile-stat-item" title="Number of batches assigned to you that are currently active">
                  <div className="profile-stat-icon" style={{ background: 'var(--brand-primary)' }}>📚</div>
                  <div className="profile-stat-content">
                    <div className="profile-stat-label">Active Courses</div>
                    <div className="profile-stat-value">{stats.activeCourses}</div>
                  </div>
                </div>
                <div className="profile-stat-item" title="Distinct active students enrolled across all your batches">
                  <div className="profile-stat-icon" style={{ background: 'var(--status-success)' }}>👥</div>
                  <div className="profile-stat-content">
                    <div className="profile-stat-label">Total Students</div>
                    <div className="profile-stat-value">{stats.totalStudents}</div>
                  </div>
                </div>
                <div className="profile-stat-item" title="Incomplete lesson plan steps + upcoming scheduled classes">
                  <div className="profile-stat-icon" style={{ background: 'var(--status-warning)' }}>📝</div>
                  <div className="profile-stat-content">
                    <div className="profile-stat-label">Pending Tasks</div>
                    <div className="profile-stat-value">{stats.pendingTasks}</div>
                  </div>
                </div>
                <div className="profile-stat-item" title="Total teaching hours scheduled this week (Mon\u2013Sun)">
                  <div className="profile-stat-icon" style={{ background: 'var(--brand-accent)' }}>⏰</div>
                  <div className="profile-stat-content">
                    <div className="profile-stat-label">Hours This Week</div>
                    <div className="profile-stat-value">{stats.hoursThisWeek}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Actions Card */}
          <div className="profile-card">
            <div className="profile-card-header">
              <h2>Account Actions</h2>
            </div>
            <div className="profile-card-body">
              <div className="profile-actions">
                <button className="profile-action-btn secondary">
                  <span className="profile-action-icon">🔔</span>
                  <span className="profile-action-text">Notification Settings</span>
                </button>
                <button className="profile-action-btn secondary" onClick={() => History.push('/change-password')}>
                  <span className="profile-action-icon">🔒</span>
                  <span className="profile-action-text">Change Password</span>
                </button>
                <button className="profile-action-btn secondary">
                  <span className="profile-action-icon">⚙️</span>
                  <span className="profile-action-text">Preferences</span>
                </button>
                <button className="profile-action-btn danger" onClick={handleLogout}>
                  <span className="profile-action-icon">🚪</span>
                  <span className="profile-action-text">Logout</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
